const article = {
  slug: "before-after-case-studies",
  cluster: "compare",
  title: "Before & After: How Real Listing Turnarounds Work",
  description:
    "What changes when a struggling listing gets fixed — photos, description, and price position, shown as before-and-after examples of a listing turnaround.",
  audience: "Sellers & agents",
  funnelStage: "BOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "5 min read",
  tldr: "A listing turnaround almost always comes down to three before-and-after changes: a stronger lead photo and re-sequenced images, a description that leads with the best feature instead of a spec list, and a price moved into line with recent sold comps. Made together, they reset how buyers perceive the home.",
  blocks: [
    {
      type: "callout",
      spans: [
        "The transformations below are representative, illustrative examples of how listing turnarounds typically work — not specific client results or guarantees.",
      ],
    },
    {
      type: "p",
      spans: [
        "Most listings that turn around weren't bad homes — they were badly presented. When a stale listing finally sells, you can almost always trace it to the same three before-and-after changes. Here's what each one looks like.",
      ],
    },
    {
      type: "h2",
      id: "photos",
      text: "Before & after: the photos",
    },
    {
      type: "table",
      headers: ["Before", "After"],
      rows: [
        [
          "Lead photo is a dim, gray-sky exterior or an interior shot",
          "Lead photo is a bright, straight-on hero shot of the home's best face",
        ],
        [
          "Photos in upload order — bathroom before living room",
          "Photos sequenced to tell a story: exterior, living, kitchen, primary suite",
        ],
        [
          "Cluttered counters, brown lawn, overcast light",
          "Decluttered frames, greened lawn, sunny exterior (AI touch-ups on real photos)",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "The lead photo does most of the work, because it decides whether a buyer clicks at all. See ",
        {
          text: "the best order for listing photos",
          href: "/resources/best-order-for-listing-photos",
        },
        " and ",
        {
          text: "AI photo touch-ups vs virtual staging",
          href: "/resources/ai-photo-editing-vs-virtual-staging",
        },
        " for what's fair to change — and note that altering the sky, lawn, or other real elements may require MLS disclosure, with the original kept available and rules varying by state and MLS.",
      ],
    },
    {
      type: "h2",
      id: "description",
      text: "Before & after: the description",
    },
    {
      type: "table",
      headers: ["Before", "After"],
      rows: [
        [
          '"3 bed, 2 bath home with a 2-car garage and large yard."',
          '"Mornings start on the west-facing deck overlooking a fenced, oversized yard — then an easy walk to the kitchen\'s oversized island."',
        ],
        [
          "Leads with a spec list the photos already show",
          "Leads with the single best feature and a lifestyle the buyer can picture",
        ],
        [
          "Generic adjectives: spacious, cozy, must-see",
          "Concrete specifics buyers can verify and remember",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "The full method is in ",
        {
          text: "how to write a listing description that sells",
          href: "/resources/how-to-write-a-listing-description-that-sells",
        },
        " and ",
        { text: "words that sell a house", href: "/resources/words-that-sell-a-house" },
        ".",
      ],
    },
    {
      type: "h2",
      id: "price",
      text: "Before & after: the price position",
    },
    {
      type: "p",
      spans: [
        "The quietest change is often the most important. A home priced just above the range of recent sold comps gets skipped; the same home repriced into that range — in one decisive move, not a slow trickle of cuts — starts getting showings again. See ",
        { text: "is my house overpriced", href: "/resources/is-my-house-overpriced" },
        " and ",
        { text: "how to relist a house", href: "/resources/how-to-relist-a-house" },
        ".",
      ],
    },
    {
      type: "h2",
      id: "together",
      text: "Why you change them together",
    },
    {
      type: "p",
      spans: [
        "Fixing one thing at a time wastes the relaunch. A buyer who already skipped your listing won't come back for a single new photo. The turnaround works when the photos, description, and price reset at once, so returning and new buyers meet a listing that looks and prices like a different home. A ",
        { text: "Listino listing prep", href: "/get-started" },
        " produces all three changes in one pass.",
      ],
    },
  ],
  faqs: [
    {
      question: "How much does a listing turnaround usually involve?",
      answer: [
        {
          type: "p",
          spans: [
            "Usually less than sellers expect: better photos and sequencing, a rewritten description, and a price corrected to the comps. The work is mostly presentation and positioning, not renovation.",
          ],
        },
      ],
    },
    {
      question: "Can you reset a listing without dropping the price?",
      answer: [
        {
          type: "p",
          spans: [
            "Sometimes — if the original price was actually fair and the problem was presentation. But if the home was getting views and saves yet no showings, price is usually part of the reset. See ",
            { text: "views but no showings", href: "/resources/views-but-no-showings" },
            ".",
          ],
        },
      ],
    },
  ],
  related: [
    "how-to-relist-a-house",
    "ai-photo-editing-vs-virtual-staging",
    "best-order-for-listing-photos",
    "roi-of-optimizing-a-listing",
  ],
};

export default article;
